import React, { useState, useEffect } from 'react';
import { teacherAPI } from '../../services/api';
import toast from 'react-hot-toast';

const TeacherGrading = () => {
  const [assignedSubjects, setAssignedSubjects] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedClass, setSelectedClass] = useState('');
  const [academicSessions, setAcademicSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [students, setStudents] = useState([]);
  const [assessmentFields, setAssessmentFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gradingSummary, setGradingSummary] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load initial data
  useEffect(() => {
    fetchAssignedSubjects();
    fetchAcademicSessions();
  }, []);

  // Load classes when subject changes
  useEffect(() => {
    if (selectedSubject) {
      fetchClassesForSubject();
    }
  }, [selectedSubject]);

  // Load students when all selections are made
  useEffect(() => {
    if (selectedSubject && selectedClass && selectedSession && selectedTerm) {
      fetchStudentsForGrading();
      fetchGradingSummary();
    }
  }, [selectedSubject, selectedClass, selectedSession, selectedTerm]);

  const fetchAssignedSubjects = async () => {
    try {
      const response = await teacherAPI.getAssignedSubjects();
      setAssignedSubjects(response.data);
    } catch (error) {
      toast.error('Failed to load assigned subjects');
    }
  };

  const fetchAcademicSessions = async () => {
    try {
      const response = await teacherAPI.getAcademicSessions();
      setAcademicSessions(response.data);
    } catch (error) {
      toast.error('Failed to load academic sessions');
    }
  };

  // const fetchClassesForSubject = async () => {
  //   try {
  //     const response = await teacherAPI.getClassesForSubject(selectedSubject);
  //     // Handle response
  //   } catch (error) {
  //     toast.error('Failed to load classes for subject');
  //   }
  // };

  const fetchClassesForSubject = async () => {
  try {
    const response = await teacherAPI.getClassesForSubject(selectedSubject);
    // You can store the classes in state if needed
    // setClassesForSubject(response.data);
  } catch (error) {
    toast.error('Failed to load classes for subject');
  }
};

  const fetchStudentsForGrading = async () => {
    try {
      setLoading(true);
      const response = await teacherAPI.getStudentsForGrading(
        selectedClass,
        selectedSubject,
        selectedSession,
        selectedTerm
      );
      
      setStudents(response.data.students);
      setAssessmentFields(response.data.assessment_fields);
    } catch (error) {
      toast.error('Failed to load students for grading');
    } finally {
      setLoading(false);
    }
  };

  const fetchGradingSummary = async () => {
    try {
      const response = await teacherAPI.getGradingSummary(
        selectedClass,
        selectedSubject,
        selectedSession,
        selectedTerm
      );
      setGradingSummary(response.data);
    } catch (error) {
      console.error('Failed to load grading summary:', error);
    }
  };

  const handleScoreChange = (studentReg, fieldCode, value) => {
    setStudents(prevStudents => 
      prevStudents.map(student => {
        if (student.student_reg === studentReg) {
          const updatedScores = student.assessment_scores.map(score => 
            score.field_code === fieldCode 
              ? { ...score, score: parseFloat(value) || 0 }
              : score
          );
          
          // Recalculate total score
          const totalScore = updatedScores.reduce(
            (sum, score) => sum + (score.score * score.weight), 0
          );
          
          return {
            ...student,
            assessment_scores: updatedScores,
            calculated_total_score: Math.round(totalScore * 100) / 100
          };
        }
        return student;
      })
    );
  };

  const handleSubmitGrades = async () => {
    // Validate all compulsory fields
    const validationErrors = [];
    
    students.forEach(student => {
      student.assessment_scores.forEach(score => {
        const field = assessmentFields.find(f => f.field_code === score.field_code);
        if (field && field.is_compulsory && (!score.score || score.score === '')) {
          validationErrors.push({
            student: student.name,
            field: field.display_name
          });
        }
      });
    });
    
    if (validationErrors.length > 0) {
      toast.error(`Please fill all compulsory fields for all students`);
      return;
    }
    
    // Prepare submission data
    const studentGrades = students.map(student => ({
      student_reg: student.student_reg,
      scores: student.assessment_scores.map(score => ({
        field_code: score.field_code,
        score: parseFloat(score.score) || 0
      }))
    }));
    
    try {
      setIsSubmitting(true);
      const response = await teacherAPI.submitGrades(
        selectedClass,
        selectedSubject,
        selectedSession,
        selectedTerm,
        { student_grades: studentGrades }
      );
      
      toast.success(response.data.message);
      // Refresh data
      fetchStudentsForGrading();
      fetchGradingSummary();
    } catch (error) {
      toast.error('Failed to submit grades');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getSubjectName = () => {
    const subject = assignedSubjects.find(s => s.subject_id == selectedSubject);
    return subject ? subject.subject_name : 'Unknown Subject';
  };

  const getClassName = () => {
    const subject = assignedSubjects.find(s => 
      s.subject_id == selectedSubject && s.class_id == selectedClass
    );
    return subject ? subject.class_name : 'Unknown Class';
  };

  const getSessionName = () => {
    const session = academicSessions.find(s => s.id == selectedSession);
    return session ? session.academic_session : 'Unknown Session';
  };

  const getTermName = () => {
    if (!selectedSession) return 'Unknown Term';
    const session = academicSessions.find(s => s.id == selectedSession);
    if (session) {
      const term = session.terms.find(t => t.id == selectedTerm);
      return term ? term.term : 'Unknown Term';
    }
    return 'Unknown Term';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Student Grading System</h1>
          <p className="text-gray-600">Enter and manage student grades by subject and class</p>
        </div>
      </div>

      {/* Selection Panel */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Subject *
            </label>
            <select
              value={selectedSubject}
              onChange={(e) => {
                setSelectedSubject(e.target.value);
                setSelectedClass('');
                setStudents([]);
              }}
              className="input-field"
              disabled={loading}
            >
              <option value="">Choose a subject</option>
              {assignedSubjects.map((subject) => (
                <option key={subject.subject_id} value={subject.subject_id}>
                  {subject.subject_name} ({subject.subject_code})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Class *
            </label>
            <select
              value={selectedClass}
              onChange={(e) => {
                setSelectedClass(e.target.value);
                setStudents([]);
              }}
              className="input-field"
              disabled={!selectedSubject || loading}
            >
              <option value="">Choose a class</option>
              {assignedSubjects
                .filter(s => s.subject_id == selectedSubject)
                .map((subject) => (
                  <option key={subject.class_id} value={subject.class_id}>
                    {subject.class_name}
                  </option>
                ))
              }
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Academic Session *
            </label>
            <select
              value={selectedSession}
              onChange={(e) => {
                setSelectedSession(e.target.value);
                setSelectedTerm('');
                setStudents([]);
              }}
              className="input-field"
              disabled={!selectedClass || loading}
            >
              <option value="">Choose session</option>
              {academicSessions.map((session) => (
                <option key={session.id} value={session.id}>
                  {session.academic_session}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Term *
            </label>
            <select
              value={selectedTerm}
              onChange={(e) => {
                setSelectedTerm(e.target.value);
                setStudents([]);
              }}
              className="input-field"
              disabled={!selectedSession || loading}
            >
              <option value="">Choose term</option>
              {selectedSession && academicSessions
                .find(s => s.id == selectedSession)
                ?.terms.map((term) => (
                  <option key={term.id} value={term.id}>
                    {term.term}
                  </option>
                ))
              }
            </select>
          </div>
        </div>

        {selectedSubject && selectedClass && selectedSession && selectedTerm && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-700">
                  Grading for <strong>{getSubjectName()}</strong> in{' '}
                  <strong>{getClassName()}</strong> | {getSessionName()} - {getTermName()}
                </p>
              </div>
              {gradingSummary && (
                <div className="text-sm text-blue-600">
                  {gradingSummary.graded_students}/{gradingSummary.total_students} students graded
                  ({gradingSummary.completion_rate}%)
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Grading Summary */}
      {gradingSummary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Total Students</div>
            <div className="text-2xl font-bold text-gray-800">{gradingSummary.total_students}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Graded</div>
            <div className="text-2xl font-bold text-green-600">{gradingSummary.graded_students}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Pending</div>
            <div className="text-2xl font-bold text-yellow-600">{gradingSummary.pending_students}</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-500">Average Score</div>
            <div className="text-2xl font-bold text-blue-600">{gradingSummary.average_score}</div>
          </div>
        </div>
      )}

      {/* Grading Table */}
      {students.length > 0 && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-800">Student Grades</h3>
            <button
              onClick={handleSubmitGrades}
              disabled={isSubmitting}
              className="btn-primary px-4 py-2"
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Submitting...
                </>
              ) : (
                <>
                  <i className="fas fa-save mr-2"></i>
                  Save All Grades
                </>
              )}
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Student
                  </th>
                  {assessmentFields.map((field) => (
                    <th key={field.field_code} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {field.display_name}
                      {field.is_compulsory && <span className="text-red-500 ml-1">*</span>}
                      <div className="text-xs text-gray-400">Max: {field.max_score}</div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Score
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Grade
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {students.map((student) => {
                  const grade = _calculateGrade(student.calculated_total_score);
                  const gradeColor = getGradeColor(grade);
                  
                  return (
                    <tr key={student.student_reg} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {student.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {student.student_reg}
                          </div>
                        </div>
                      </td>
                      
                      {assessmentFields.map((field) => {
                        const score = student.assessment_scores.find(
                          s => s.field_code === field.field_code
                        ) || { score: 0 };
                        
                        return (
                          <td key={field.field_code} className="px-4 py-4">
                            <input
                              type="number"
                              min={field.min_score || 0}
                              max={field.max_score}
                              step="0.5"
                              value={score.score}
                              onChange={(e) => handleScoreChange(
                                student.student_reg,
                                field.field_code,
                                e.target.value
                              )}
                              className={`w-20 px-2 py-1 border rounded ${
                                field.is_compulsory && !score.score 
                                  ? 'border-red-300 bg-red-50' 
                                  : 'border-gray-300'
                              }`}
                            />
                          </td>
                        );
                      })}
                      
                      <td className="px-4 py-4">
                        <div className="text-lg font-bold text-gray-900">
                          {student.calculated_total_score.toFixed(2)}
                        </div>
                      </td>
                      <td className="px-4 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${gradeColor}`}>
                          {grade}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          
          <div className="p-4 border-t bg-gray-50">
            <div className="text-sm text-gray-600">
              <span className="text-red-500">*</span> Compulsory fields must be filled for all students
            </div>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && students.length === 0 && selectedSubject && selectedClass && selectedSession && selectedTerm && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <i className="fas fa-graduation-cap text-4xl text-gray-300 mb-4"></i>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No Students Found</h3>
          <p className="text-gray-500 max-w-md mx-auto">
            There are no students registered in this class for the selected academic session and term.
          </p>
        </div>
      )}
    </div>
  );
};

// Helper functions
const _calculateGrade = (score) => {
  if (score >= 90) return "A+";
  if (score >= 80) return "A";
  if (score >= 70) return "B";
  if (score >= 60) return "C";
  if (score >= 50) return "D";
  return "F";
};

const getGradeColor = (grade) => {
  if (grade === 'A+') return 'bg-green-100 text-green-800';
  if (grade.startsWith('A')) return 'bg-green-100 text-green-800';
  if (grade.startsWith('B')) return 'bg-blue-100 text-blue-800';
  if (grade.startsWith('C')) return 'bg-yellow-100 text-yellow-800';
  if (grade.startsWith('D')) return 'bg-orange-100 text-orange-800';
  return 'bg-red-100 text-red-800';
};

export default TeacherGrading;